/**
 * Calibration Procedure Table Component
 * Data table following the ATEK table-style pattern (Mode B: server-side hybrid)
 */

import { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Trash2,
  Download,
  Copy,
  Settings2,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatDate } from '@/lib/utils';
import type { CalibrationProcedure } from '../types';
import { PROCEDURE_TAGS } from '../types';
import { exportToCSV, copyToClipboard } from '../utils/csvExport';

// =============================================================================
// ColumnDef
// =============================================================================

interface ColumnDef<T> {
  key: string;
  label: string;
  defaultVisible?: boolean;
  sortKey?: string;
  filterType?: 'text' | 'date-range' | 'select' | 'number-range';
  filterOptions?: { label: string; value: string }[];
  getValue: (item: T) => string | number | null;
  render?: (item: T) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  noFilter?: boolean;
  className?: string;
}

// =============================================================================
// Column Definitions
// =============================================================================

const COLUMNS: ColumnDef<CalibrationProcedure>[] = [
  {
    key: 'name',
    label: 'Name',
    sortKey: 'name',
    filterType: 'text',
    getValue: (p) => p.name,
    render: (p) => <span className="font-medium">{p.name}</span>,
  },
  {
    key: 'dueDateMonths',
    label: 'Due Date',
    filterType: 'number-range',
    getValue: (p) => p.dueDateMonths,
    render: (p) => (
      <span className="text-sm">
        {p.dueDateMonths} {p.dueDateMonths === 1 ? 'month' : 'months'}
      </span>
    ),
  },
  {
    key: 'tags',
    label: 'Tags',
    filterType: 'select',
    filterOptions: PROCEDURE_TAGS.map((tag) => ({ label: tag, value: tag })),
    getValue: (p) => p.tags.join(', '),
    render: (p) =>
      p.tags.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {p.tags.map((tag) => (
            <Badge key={tag} variant="secondary">
              {tag}
            </Badge>
          ))}
        </div>
      ) : (
        <span className="text-muted-foreground">&mdash;</span>
      ),
  },
  {
    key: 'createdAt',
    label: 'Created At',
    sortKey: 'created_at',
    filterType: 'date-range',
    getValue: (p) => p.createdAt,
    render: (p) => <span className="text-sm">{formatDate(p.createdAt)}</span>,
  },
  {
    key: 'status',
    label: 'Status',
    filterType: 'select',
    filterOptions: [
      { label: 'Enabled', value: 'enabled' },
      { label: 'Deleted', value: 'deleted' },
    ],
    getValue: (p) => (p.deletedAt ? 'deleted' : 'enabled'),
    render: (p) =>
      p.deletedAt ? (
        <Badge variant="danger">Deleted</Badge>
      ) : (
        <Badge variant="success">Enabled</Badge>
      ),
    align: 'center',
  },
];

// =============================================================================
// localStorage Helpers
// =============================================================================

const FILTER_STORAGE_KEY = 'atek-calibration-procedures-column-filters';
const VISIBILITY_STORAGE_KEY = 'atek-calibration-procedures-column-visibility';

function getDefaultVisibility(): Record<string, boolean> {
  const defaults: Record<string, boolean> = {};
  for (const col of COLUMNS) {
    defaults[col.key] = col.defaultVisible !== false;
  }
  return defaults;
}

function loadColumnVisibility(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (stored) return { ...getDefaultVisibility(), ...JSON.parse(stored) };
  } catch {
    /* ignore */
  }
  return getDefaultVisibility();
}

function saveColumnVisibility(visibility: Record<string, boolean>) {
  localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
}

function loadColumnFilters(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(FILTER_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

// =============================================================================
// SortableHeader
// =============================================================================

function SortableHeader({
  label,
  sortKey,
  currentSortKey,
  sortDirection,
  onSort,
}: {
  label: string;
  sortKey: string;
  currentSortKey?: string;
  sortDirection?: number;
  onSort: (key: string) => void;
}) {
  const isActive = currentSortKey === sortKey;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="-ml-3 h-8 hover:bg-transparent"
      onClick={() => onSort(sortKey)}
    >
      {label}
      {isActive ? (
        sortDirection === 1 ? (
          <ArrowUp className="ml-1 h-3 w-3" />
        ) : (
          <ArrowDown className="ml-1 h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
      )}
    </Button>
  );
}

// =============================================================================
// Filter Row Renderer
// =============================================================================

function renderColumnFilter<T>(
  col: ColumnDef<T>,
  columnFilters: Record<string, string>,
  setColumnFilter: (key: string, value: string) => void,
) {
  if (col.noFilter) return <TableHead key={`filter-${col.key}`} />;

  // Multi-select filter
  if (col.filterType === 'select' && col.filterOptions) {
    const selected: string[] = (() => {
      try {
        return columnFilters[col.key] ? JSON.parse(columnFilters[col.key]) : [];
      } catch {
        return [];
      }
    })();

    const toggle = (opt: string) => {
      const next = selected.includes(opt)
        ? selected.filter((o) => o !== opt)
        : [...selected, opt];
      setColumnFilter(col.key, next.length ? JSON.stringify(next) : '');
    };

    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-7 w-full text-xs justify-start font-normal"
            >
              {selected.length ? `${selected.length} selected` : '\u2014'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-44">
            {col.filterOptions.map((opt) => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selected.includes(opt.value)}
                onCheckedChange={() => toggle(opt.value)}
                onSelect={(e) => e.preventDefault()}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </TableHead>
    );
  }

  // Date range filter
  if (col.filterType === 'date-range') {
    const raw = columnFilters[col.key] || '';
    const [from = '', to = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={from}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${to}`)}
            title="From"
          />
          <Input
            type="date"
            className="h-6 text-xs px-1"
            value={to}
            onChange={(e) => setColumnFilter(col.key, `${from}>>${e.target.value}`)}
            title="To"
          />
        </div>
      </TableHead>
    );
  }

  // Number range filter
  if (col.filterType === 'number-range') {
    const raw = columnFilters[col.key] || '';
    const [min = '', max = ''] = raw.split('>>');
    return (
      <TableHead key={`filter-${col.key}`} className="p-1">
        <div className="flex flex-col gap-0.5">
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={min}
            onChange={(e) => setColumnFilter(col.key, `${e.target.value}>>${max}`)}
            placeholder="Min"
          />
          <Input
            type="number"
            className="h-6 text-xs px-1"
            value={max}
            onChange={(e) => setColumnFilter(col.key, `${min}>>${e.target.value}`)}
            placeholder="Max"
          />
        </div>
      </TableHead>
    );
  }

  // Text filter (default)
  return (
    <TableHead key={`filter-${col.key}`} className="p-1">
      <Input
        className="h-7 text-xs"
        value={columnFilters[col.key] || ''}
        onChange={(e) => setColumnFilter(col.key, e.target.value)}
        placeholder="..."
      />
    </TableHead>
  );
}

// =============================================================================
// Table Component
// =============================================================================

interface CalibrationProcedureTableProps {
  procedures: CalibrationProcedure[];
  total: number;
  page: number;
  pageLength: number;
  sortKey: string;
  sortDirection: number;
  onPageChange: (page: number) => void;
  onSortChange: (key: string, direction: number) => void;
  onEdit: (procedure: CalibrationProcedure) => void;
  onDelete: (procedure: CalibrationProcedure) => void;
  isLoading: boolean;
}

export function CalibrationProcedureTable({
  procedures,
  total,
  page,
  pageLength,
  sortKey,
  sortDirection,
  onPageChange,
  onSortChange,
  onEdit,
  onDelete,
  isLoading,
}: CalibrationProcedureTableProps) {
  const totalPages = Math.ceil(total / pageLength);

  // ---------------------------------------------------------------------------
  // Sort handler
  // ---------------------------------------------------------------------------
  const handleSort = (key: string) => {
    if (key === sortKey) {
      onSortChange(key, sortDirection === 1 ? -1 : 1);
    } else {
      onSortChange(key, 1);
    }
  };

  // ---------------------------------------------------------------------------
  // Actions column (needs props)
  // ---------------------------------------------------------------------------
  const actionsColumn: ColumnDef<CalibrationProcedure> = {
    key: 'actions',
    label: '',
    noFilter: true,
    getValue: () => '',
    render: (p) => (
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(p);
          }}
          className="text-atek-blue hover:underline text-sm cursor-pointer"
        >
          Edit
        </button>
        {!p.deletedAt && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(p);
            }}
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        )}
      </div>
    ),
    align: 'right',
  };

  const allColumns: ColumnDef<CalibrationProcedure>[] = [...COLUMNS, actionsColumn];

  // ---------------------------------------------------------------------------
  // Column filters (client-side refinement, persisted to localStorage)
  // ---------------------------------------------------------------------------
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(loadColumnFilters);

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => {
      const next = { ...prev, [key]: value };
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  // ---------------------------------------------------------------------------
  // Column visibility (persisted to localStorage)
  // ---------------------------------------------------------------------------
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(loadColumnVisibility);

  const toggleColumn = (key: string) => {
    const updated = { ...columnVisibility, [key]: !columnVisibility[key] };
    setColumnVisibility(updated);
    saveColumnVisibility(updated);
  };

  const isVisible = (key: string) => columnVisibility[key] !== false;

  const visibleColumns = allColumns.filter((col) => col.noFilter || isVisible(col.key));

  // ---------------------------------------------------------------------------
  // Combined AND filtering (client-side refinement within server page)
  // ---------------------------------------------------------------------------
  const filteredProcedures = useMemo(() => {
    const activeFilters = Object.entries(columnFilters).filter(([, v]) => v);
    if (activeFilters.length === 0) return procedures;

    return procedures.filter((item) =>
      activeFilters.every(([colKey, filterValue]) => {
        const col = COLUMNS.find((c) => c.key === colKey);
        if (!col || col.noFilter) return true;

        const rawValue = col.getValue(item);

        // Date range: "from>>to"
        if (col.filterType === 'date-range') {
          const [from, to] = filterValue.split('>>');
          const dateStr = String(rawValue || '');
          if (from && dateStr < from) return false;
          if (to && dateStr > to + 'T23:59:59') return false;
          return true;
        }

        // Multi-select: JSON array
        if (col.filterType === 'select') {
          try {
            const selected = JSON.parse(filterValue) as string[];
            if (selected.length === 0) return true;
            const rawStr = String(rawValue || '');
            // Support multi-value fields (comma-separated, e.g. tags)
            const values = rawStr.includes(', ') ? rawStr.split(', ') : [rawStr];
            return values.some((v) => selected.includes(v));
          } catch {
            return true;
          }
        }

        // Number range: "min>>max"
        if (col.filterType === 'number-range') {
          const [minStr, maxStr] = filterValue.split('>>');
          const num = Number(rawValue);
          if (isNaN(num)) return false;
          if (minStr && num < Number(minStr)) return false;
          if (maxStr && num > Number(maxStr)) return false;
          return true;
        }

        // Text: case-insensitive includes
        return String(rawValue ?? '')
          .toLowerCase()
          .includes(filterValue.toLowerCase());
      }),
    );
  }, [procedures, columnFilters]);

  // ---------------------------------------------------------------------------
  // Export handlers
  // ---------------------------------------------------------------------------
  const handleCopy = () => {
    copyToClipboard(filteredProcedures, allColumns);
    toast.success('Copied to clipboard');
  };

  const handleExportCSV = () => {
    exportToCSV(filteredProcedures, allColumns, 'calibration-procedures');
    toast.success(`Exported ${filteredProcedures.length} rows`);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardDescription>
            {filteredProcedures.length < procedures.length
              ? `Showing ${filteredProcedures.length} of ${procedures.length} on this page`
              : `Showing ${procedures.length} of ${total}`}
          </CardDescription>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleCopy}>
              <Copy className="h-4 w-4 mr-1" />
              Copy
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportCSV}>
              <Download className="h-4 w-4 mr-1" />
              CSV
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings2 className="h-4 w-4 mr-1" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {COLUMNS.map((col) => (
                  <DropdownMenuCheckboxItem
                    key={col.key}
                    checked={isVisible(col.key)}
                    onCheckedChange={() => toggleColumn(col.key)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {col.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              {/* ROW 1: Filter inputs (always visible, only visible columns) */}
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                {visibleColumns.map((col) =>
                  renderColumnFilter(col, columnFilters, setColumnFilter),
                )}
              </TableRow>

              {/* ROW 2: Column labels + sort controls */}
              <TableRow>
                {visibleColumns.map((col) => (
                  <TableHead
                    key={col.key}
                    className={cn(
                      col.align === 'right' && 'text-right',
                      col.align === 'center' && 'text-center',
                    )}
                  >
                    {col.sortKey ? (
                      <SortableHeader
                        label={col.label}
                        sortKey={col.sortKey}
                        currentSortKey={sortKey}
                        sortDirection={sortDirection}
                        onSort={handleSort}
                      />
                    ) : (
                      col.label
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {/* Loading state */}
              {isLoading &&
                Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    {visibleColumns.map((col) => (
                      <TableCell key={col.key}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {/* Empty state */}
              {!isLoading && filteredProcedures.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumns.length}
                    className="py-12 text-center text-muted-foreground"
                  >
                    No calibration procedures found
                  </TableCell>
                </TableRow>
              )}

              {/* Data rows */}
              {!isLoading &&
                filteredProcedures.map((procedure) => (
                  <TableRow key={procedure.id} className="hover:bg-muted/50">
                    {visibleColumns.map((col) => (
                      <TableCell
                        key={col.key}
                        className={cn(
                          'text-sm',
                          col.align === 'right' && 'text-right',
                          col.align === 'center' && 'text-center',
                          col.className,
                        )}
                      >
                        {col.render
                          ? col.render(procedure)
                          : String(col.getValue(procedure) ?? '') || '\u2014'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Page {page + 1} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(page - 1)}
                disabled={page === 0}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages - 1}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
